import type { PhotoFrameItem } from '@/schemas/v2/spot'
import { memo, useEffect, useRef } from 'react'
import { PhotoFrame } from './PhotoFrame'

type Props = {
  photoFrames: PhotoFrameItem[]
  open: boolean
  onClose: () => void
  onSaveSuccess?: () => void
  scrollLock?: boolean
  snsHashtag?: string
  /** 撮影後画面の拡散用SNS（context=ar_photo）取得に使うスポットID */
  spotId?: number
  /** AR起動時に自動再生する音声URL（#3261）。再生完了まで撮影・録画を無効化する。無い場合は即撮影可 */
  audioUrl?: string | null
}

export const ArPhotoOverlay = memo(
  ({ photoFrames, open, onClose, onSaveSuccess, snsHashtag, spotId, audioUrl }: Props) => {
    // 最新の onClose を ref で保持し、identity 変化で履歴管理 effect が再実行されないようにする。
    // effect が onClose に依存すると、inline 関数を渡す呼び出し元では親の再レンダーごとに
    // cleanup の history.back()（非同期）と pushState（同期）が対で走り、処理順によって
    // ゴミ履歴エントリが残って「戻るが効かない」状態になる（ScanCodeOverlay と同じ対策）。
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
      if (!open) return
      // オーバーレイ表示時に履歴エントリを追加
      history.pushState({ arPhotoOverlay: true }, '')
      let closedViaPopState = false
      const handlePopState = () => {
        closedViaPopState = true
        onCloseRef.current()
      }
      window.addEventListener('popstate', handlePopState)
      return () => {
        window.removeEventListener('popstate', handlePopState)
        // popstate 経由でない閉じ方の場合は、push した履歴エントリを戻す
        if (!closedViaPopState && history.state?.arPhotoOverlay) {
          history.back()
        }
      }
    }, [open])

    if (!open) return null

    return (
      <div className="fixed inset-0 z-[200] flex flex-col">
        <PhotoFrame
          photoFrame={photoFrames}
          audioUrl={audioUrl}
          snsHashtag={snsHashtag}
          spotId={spotId}
          onSaveSuccess={() => {
            onSaveSuccess?.()
            onClose()
          }}
          onClose={() => {
            // × ボタンで閉じる場合は履歴を戻す
            if (history.state?.arPhotoOverlay) {
              history.back()
            } else {
              onClose()
            }
          }}
        />
      </div>
    )
  },
)

ArPhotoOverlay.displayName = 'ArPhotoOverlay'
